import { useRef } from "react";
import {
    Plus,
    Trash2,
    Upload,
    Loader2,
    Volume2,
    VolumeX,
    Bold,
    Italic,
    Circle,
    Square,
    Sparkles,
    MessageSquare,
} from "lucide-react";
import { FILTERS, fmtTime } from "@/lib/filters";
import { isEffectLocked } from "@/lib/effects";

export const ADJ_DEFS = [
    { id: "brightness", label: "Brightness", min: 50, max: 150, unit: "%" },
    { id: "contrast", label: "Contrast", min: 50, max: 200, unit: "%" },
    { id: "saturation", label: "Saturation", min: 0, max: 250, unit: "%" },
    { id: "exposure", label: "Exposure", min: 50, max: 150, unit: "%" },
    { id: "highlights", label: "Highlights", min: -50, max: 50, unit: "" },
    { id: "shadows", label: "Shadows", min: -50, max: 50, unit: "" },
    { id: "temperature", label: "Temperature", min: -50, max: 50, unit: "" },
    { id: "tint", label: "Tint", min: -50, max: 50, unit: "" },
    { id: "sharpness", label: "Sharpness", min: 0, max: 100, unit: "%" },
    { id: "blur", label: "Soft Focus", min: 0, max: 10, unit: "px" },
    { id: "hue", label: "Hue Shift", min: -180, max: 180, unit: "°" },
    { id: "vignette", label: "Vignette", min: 0, max: 100, unit: "%" },
    { id: "fade", label: "Fade", min: 0, max: 100, unit: "%" },
];

export function SliderRow({ label, value, min, max, step = 1, display, onChange, testid, disabled }) {
    return (
        <div>
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-300">{label}</span>
                <span className="font-mono text-[11px] text-amberx">{display}</span>
            </div>
            <input
                type="range"
                className="slider"
                data-testid={testid}
                min={min}
                max={max}
                step={step}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(Number(e.target.value))}
            />
        </div>
    );
}

function Toggle({ label, active, onClick, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`h-11 rounded-xl border text-xs font-bold transition-colors ${
                active ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-300 hover:border-white/40"
            }`}
        >
            {label}
        </button>
    );
}

/* ---------------- FILTERS + ADJUST ---------------- */

export function FiltersPanel({ filterId, onFilter, thumbs, intensity, onIntensity, adj, onAdj }) {
    return (
        <div className="space-y-4">
            <div>
                <p className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">FILTERS</p>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {FILTERS.map((f) => {
                        const selected = filterId === f.id;
                        return (
                            <button
                                key={f.id}
                                data-testid={`filter-chip-${f.id}`}
                                onClick={() => onFilter(f.id)}
                                className={`overflow-hidden rounded-xl border transition-all ${
                                    selected ? "border-amberx shadow-[0_0_14px_rgba(255,159,28,0.3)]" : "border-white/10 hover:border-white/35"
                                }`}
                            >
                                <span className="relative block aspect-[3/4] w-full bg-obsidian">
                                    {thumbs[f.id] ? (
                                        <img src={thumbs[f.id]} alt={`${f.name} preview`} className="h-full w-full object-cover" />
                                    ) : (
                                        <span className="flex h-full w-full items-center justify-center">
                                            <Loader2 className="h-4 w-4 animate-spin text-slate-600" />
                                        </span>
                                    )}
                                </span>
                                <span className={`block truncate px-1.5 py-1 text-center text-[10px] font-bold ${selected ? "bg-amberx text-obsidian" : "text-slate-300"}`}>
                                    {f.name}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
            <div>
                <p className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">FILTER INTENSITY</p>
                <SliderRow
                    label="Intensity"
                    testid="filter-intensity-slider"
                    min={0}
                    max={100}
                    value={intensity}
                    display={`${intensity}%`}
                    onChange={onIntensity}
                    disabled={filterId === "none"}
                />
            </div>
            <div>
                <p className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">ADJUST</p>
                <div className="space-y-1">
                    {ADJ_DEFS.map((d) => (
                        <SliderRow
                            key={d.id}
                            label={d.label}
                            testid={`adjust-${d.id}-slider`}
                            min={d.min}
                            max={d.max}
                            step={d.id === "blur" ? 0.5 : 1}
                            value={adj[d.id]}
                            display={`${adj[d.id]}${d.unit}`}
                            onChange={(v) => onAdj(d.id, v)}
                        />
                    ))}
                </div>
                <button
                    data-testid="adjust-reset-button"
                    onClick={() => onAdj(null)}
                    className="mt-2 h-10 w-full rounded-xl border border-white/15 text-xs font-bold text-slate-200 hover:border-amberx hover:text-amberx"
                >
                    Reset adjustments
                </button>
            </div>
        </div>
    );
}

/* ---------------- TEXT + STICKERS ---------------- */

const FONTS = ["Outfit", "Plus Jakarta Sans", "JetBrains Mono", "Georgia", "Arial"];
const STICKERS = ["🔥", "😂", "❤️", "👀", "💯", "✨", "🎬", "🎵", "⭐", "😍", "🤯", "👏", "🫶", "🥶", "🚀", "🏆"];

export function TextPanel({ items, selectedId, onSelect, onAdd, onPatch, onRemove, duration }) {
    const sel = items.find((t) => t.id === selectedId && t.type === "text");
    return (
        <div className="space-y-4">
            <button
                data-testid="text-add-button"
                onClick={onAdd}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-amberx font-display text-sm font-bold text-obsidian"
            >
                <Plus className="h-4 w-4" />
                Add text
            </button>
            {items.filter((t) => t.type === "text").length > 0 && (
                <div className="space-y-1.5">
                    {items
                        .filter((t) => t.type === "text")
                        .map((t) => (
                            <div
                                key={t.id}
                                className={`flex items-center justify-between rounded-xl border px-3 py-2 ${
                                    selectedId === t.id ? "border-amberx" : "border-white/10"
                                }`}
                            >
                                <button onClick={() => onSelect(t.id)} className="min-w-0 flex-1 text-left">
                                    <span className="block truncate text-sm font-semibold text-slate-200">{t.content || "Empty text"}</span>
                                    <span className="block font-mono text-[10px] text-slate-500">
                                        {fmtTime(t.start).slice(0, 5)} → {fmtTime(t.end).slice(0, 5)}
                                    </span>
                                </button>
                                <button
                                    data-testid={`text-delete-${t.id}`}
                                    onClick={() => onRemove(t.id)}
                                    className="ml-2 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:text-emberx"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                </div>
            )}
            {sel && (
                <div className="space-y-3 rounded-2xl border border-amberx/40 bg-amberx/5 p-4">
                    <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-amberx">EDIT TEXT</p>
                    <textarea
                        data-testid="text-content-input"
                        value={sel.content}
                        onChange={(e) => onPatch(sel.id, { content: e.target.value })}
                        rows={2}
                        className="w-full rounded-xl border border-white/15 bg-surface px-4 py-3 text-base text-slate-100 focus:border-amberx focus:outline-none"
                        placeholder="Type your caption…"
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <select
                            data-testid="text-font-select"
                            value={sel.font}
                            onChange={(e) => onPatch(sel.id, { font: e.target.value })}
                            className="h-11 rounded-xl border border-white/15 bg-surface px-3 text-sm text-slate-100 focus:border-amberx focus:outline-none"
                        >
                            {FONTS.map((f) => (
                                <option key={f} value={f}>
                                    {f}
                                </option>
                            ))}
                        </select>
                        <input
                            data-testid="text-color-input"
                            type="color"
                            value={sel.color}
                            onChange={(e) => onPatch(sel.id, { color: e.target.value })}
                            className="h-11 w-full cursor-pointer rounded-xl border border-white/15 bg-surface"
                        />
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                        <button
                            data-testid="text-bold-toggle"
                            onClick={() => onPatch(sel.id, { bold: !sel.bold })}
                            className={`flex h-11 items-center justify-center rounded-xl border ${sel.bold ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-300"}`}
                        >
                            <Bold className="h-4 w-4" />
                        </button>
                        <button
                            data-testid="text-italic-toggle"
                            onClick={() => onPatch(sel.id, { italic: !sel.italic })}
                            className={`flex h-11 items-center justify-center rounded-xl border ${sel.italic ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-300"}`}
                        >
                            <Italic className="h-4 w-4" />
                        </button>
                        <button
                            data-testid="text-outline-toggle"
                            onClick={() => onPatch(sel.id, { outline: !sel.outline })}
                            className={`flex h-11 items-center justify-center rounded-xl border ${sel.outline ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-300"}`}
                        >
                            <Circle className="h-4 w-4" />
                        </button>
                        <button
                            data-testid="text-background-toggle"
                            onClick={() => onPatch(sel.id, { bg: !sel.bg })}
                            className={`flex h-11 items-center justify-center rounded-xl border ${sel.bg ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-300"}`}
                        >
                            <Square className="h-4 w-4" />
                        </button>
                    </div>
                    <select
                        data-testid="text-anim-select"
                        value={sel.anim}
                        onChange={(e) => onPatch(sel.id, { anim: e.target.value })}
                        className="h-11 w-full rounded-xl border border-white/15 bg-surface px-3 text-sm text-slate-100 focus:border-amberx focus:outline-none"
                    >
                        <option value="none">No animation</option>
                        <option value="fade">Fade in</option>
                        <option value="pop">Pop in</option>
                        <option value="slide">Slide in</option>
                    </select>
                    <SliderRow
                        label="Size"
                        testid="text-size-slider"
                        min={3}
                        max={22}
                        value={sel.size}
                        display={`${sel.size}%`}
                        onChange={(v) => onPatch(sel.id, { size: v })}
                    />
                    <SliderRow
                        label="Shadow"
                        testid="text-shadow-slider"
                        min={0}
                        max={1}
                        value={sel.shadow ? 1 : 0}
                        display={sel.shadow ? "On" : "Off"}
                        onChange={(v) => onPatch(sel.id, { shadow: !!v })}
                    />
                    <SliderRow
                        label="Shows at"
                        testid="text-start-slider"
                        min={0}
                        max={Math.max(0.2, duration)}
                        step={0.1}
                        value={sel.start}
                        display={fmtTime(sel.start).slice(0, 5)}
                        onChange={(v) => onPatch(sel.id, { start: Math.min(v, sel.end - 0.2) })}
                    />
                    <SliderRow
                        label="Hides at"
                        testid="text-end-slider"
                        min={0.2}
                        max={Math.max(0.2, duration)}
                        step={0.1}
                        value={sel.end}
                        display={fmtTime(sel.end).slice(0, 5)}
                        onChange={(v) => onPatch(sel.id, { end: Math.max(v, sel.start + 0.2) })}
                    />
                    <p className="font-mono text-[10px] tracking-wider text-slate-500">TIP: DRAG THE TEXT ON THE PREVIEW TO MOVE IT</p>
                </div>
            )}
        </div>
    );
}

export function StickersPanel({ items, selectedId, onSelect, onAdd, onPatch, onRemove, duration }) {
    const sel = items.find((t) => t.id === selectedId && t.type === "sticker");
    return (
        <div className="space-y-4">
            <div>
                <p className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">STICKERS</p>
                <div className="grid grid-cols-6 gap-2 sm:grid-cols-8">
                    {STICKERS.map((s) => (
                        <button
                            key={s}
                            data-testid={`sticker-add-${s.charCodeAt(0)}`}
                            onClick={() => onAdd(s)}
                            className="flex h-11 items-center justify-center rounded-xl border border-white/10 bg-surface text-2xl hover:border-amberx"
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>
            {items.filter((t) => t.type === "sticker").length > 0 && (
                <div className="space-y-1.5">
                    <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">ON TIMELINE</p>
                    {items
                        .filter((t) => t.type === "sticker")
                        .map((t) => (
                            <div
                                key={t.id}
                                className={`flex items-center justify-between rounded-xl border px-3 py-2 ${
                                    selectedId === t.id ? "border-amberx" : "border-white/10"
                                }`}
                            >
                                <button onClick={() => onSelect(t.id)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                                    <span className="text-xl">{t.content}</span>
                                    <span className="font-mono text-[10px] text-slate-500">
                                        {fmtTime(t.start).slice(0, 5)} → {fmtTime(t.end).slice(0, 5)}
                                    </span>
                                </button>
                                <button
                                    data-testid={`sticker-delete-${t.id}`}
                                    onClick={() => onRemove(t.id)}
                                    className="ml-2 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:text-emberx"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                </div>
            )}
            {sel && (
                <div className="space-y-2 rounded-2xl border border-amberx/40 bg-amberx/5 p-4">
                    <SliderRow
                        label="Size"
                        testid="sticker-size-slider"
                        min={4}
                        max={40}
                        value={sel.size}
                        display={`${sel.size}%`}
                        onChange={(v) => onPatch(sel.id, { size: v })}
                    />
                    <SliderRow
                        label="Shows at"
                        testid="sticker-start-slider"
                        min={0}
                        max={Math.max(0.2, duration)}
                        step={0.1}
                        value={sel.start}
                        display={fmtTime(sel.start).slice(0, 5)}
                        onChange={(v) => onPatch(sel.id, { start: Math.min(v, sel.end - 0.2) })}
                    />
                    <SliderRow
                        label="Hides at"
                        testid="sticker-end-slider"
                        min={0.2}
                        max={Math.max(0.2, duration)}
                        step={0.1}
                        value={sel.end}
                        display={fmtTime(sel.end).slice(0, 5)}
                        onChange={(v) => onPatch(sel.id, { end: Math.max(v, sel.start + 0.2) })}
                    />
                    <p className="font-mono text-[10px] tracking-wider text-slate-500">TIP: DRAG THE STICKER ON THE PREVIEW TO MOVE IT</p>
                </div>
            )}
        </div>
    );
}

/* ---------------- AUDIO ---------------- */

export function AudioPanel({ music, onFile, onPatch, onRemove, muteOriginal, onMuteOriginal, fileRef }) {
    return (
        <div className="space-y-4">
            <input
                ref={fileRef}
                type="file"
                accept="audio/*"
                data-testid="audio-upload-input"
                className="hidden"
                onChange={(e) => onFile(e.target.files && e.target.files[0])}
            />
            <button
                data-testid="audio-upload-button"
                onClick={() => fileRef.current && fileRef.current.click()}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-amberx font-display text-sm font-bold text-obsidian"
            >
                <Upload className="h-4 w-4" />
                {music ? "Change music" : "Add music"}
            </button>
            {music && (
                <div className="space-y-2 rounded-2xl border border-white/10 bg-surface p-4">
                    <p className="truncate text-sm font-bold text-slate-100">🎵 {music.name}</p>
                    <SliderRow
                        label="Music volume"
                        testid="audio-volume-slider"
                        min={0}
                        max={100}
                        value={Math.round(music.volume * 100)}
                        display={`${Math.round(music.volume * 100)}%`}
                        onChange={(v) => onPatch({ volume: v / 100 })}
                    />
                    <SliderRow
                        label="Fade in"
                        testid="audio-fadein-slider"
                        min={0}
                        max={5}
                        step={0.1}
                        value={music.fadeIn}
                        display={`${music.fadeIn.toFixed(1)}s`}
                        onChange={(v) => onPatch({ fadeIn: v })}
                    />
                    <SliderRow
                        label="Fade out"
                        testid="audio-fadeout-slider"
                        min={0}
                        max={5}
                        step={0.1}
                        value={music.fadeOut}
                        display={`${music.fadeOut.toFixed(1)}s`}
                        onChange={(v) => onPatch({ fadeOut: v })}
                    />
                    <SliderRow
                        label="Music starts at"
                        testid="audio-start-slider"
                        min={0}
                        max={Math.max(0.2, music.projectDur || 10)}
                        step={0.1}
                        value={music.start}
                        display={fmtTime(music.start).slice(0, 5)}
                        onChange={(v) => onPatch({ start: v })}
                    />
                    <SliderRow
                        label="Trim music start"
                        testid="audio-trimstart-slider"
                        min={0}
                        max={Math.max(0.1, music.duration - 0.5)}
                        step={0.1}
                        value={music.trimStart}
                        display={fmtTime(music.trimStart).slice(0, 5)}
                        onChange={(v) => onPatch({ trimStart: Math.min(v, music.trimEnd - 0.4) })}
                    />
                    <SliderRow
                        label="Trim music end"
                        testid="audio-trimend-slider"
                        min={0.5}
                        max={music.duration}
                        step={0.1}
                        value={music.trimEnd}
                        display={fmtTime(music.trimEnd).slice(0, 5)}
                        onChange={(v) => onPatch({ trimEnd: Math.max(v, music.trimStart + 0.4) })}
                    />
                    <button
                        data-testid="audio-remove-button"
                        onClick={onRemove}
                        className="h-10 w-full rounded-xl border border-white/15 text-xs font-bold text-slate-200 hover:border-emberx hover:text-emberx"
                    >
                        Remove music
                    </button>
                </div>
            )}
            <button
                data-testid="audio-mute-original-button"
                onClick={() => onMuteOriginal(!muteOriginal)}
                className={`flex h-12 w-full items-center justify-center gap-2 rounded-2xl border text-sm font-bold ${
                    muteOriginal ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-200"
                }`}
            >
                {muteOriginal ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                {muteOriginal ? "Original sound muted" : "Mute original sound"}
            </button>
        </div>
    );
}

/* ---------------- SPEED ---------------- */

export function SpeedPanel({ speed, onSpeed }) {
    return (
        <div>
            <p className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">CLIP SPEED</p>
            <div className="grid grid-cols-4 gap-2">
                {[0.25, 0.5, 0.75, 1, 1.5, 2, 4].map((r) => (
                    <button
                        key={r}
                        data-testid={`speed-select-${r}`}
                        onClick={() => onSpeed(r)}
                        className={`h-12 rounded-xl border font-mono text-sm font-bold ${
                            speed === r ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-200"
                        }`}
                    >
                        {r}x
                    </button>
                ))}
            </div>
        </div>
    );
}

/* ---------------- TRANSITIONS ---------------- */

export const TRANSITIONS = [
    { id: "none", label: "None" },
    { id: "tr_fade", label: "Fade" },
    { id: "tr_dissolve", label: "Dissolve" },
    { id: "tr_zoom", label: "Zoom" },
    { id: "tr_spin", label: "Spin" },
    { id: "tr_slide", label: "Slide" },
    { id: "tr_wipe", label: "Wipe" },
    { id: "tr_flash", label: "Flash" },
    { id: "tr_blur", label: "Blur" },
    { id: "tr_glitch", label: "Glitch" },
];

export function TransitionsPanel({ targetName, onSet, transDur, onDur }) {
    return (
        <div className="space-y-4">
            <p className="font-mono text-[10px] tracking-wider text-slate-500">
                ADDS AN OPENING TRANSITION TO: <span className="text-amberx">{targetName || "SELECTED CLIP"}</span>
            </p>
            <div className="grid grid-cols-3 gap-2">
                {TRANSITIONS.map((t) => (
                    <button
                        key={t.id}
                        data-testid={`transition-select-${t.id}`}
                        onClick={() => onSet(t.id, t.label)}
                        className="h-12 rounded-xl border border-white/15 font-display text-sm font-bold text-slate-200 hover:border-amberx hover:text-amberx"
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            <SliderRow
                label="Transition length"
                testid="transition-duration-slider"
                min={0.3}
                max={2.5}
                step={0.1}
                value={transDur}
                display={`${transDur.toFixed(1)}s`}
                onChange={onDur}
            />
        </div>
    );
}

/* ---------------- CAPTIONS ---------------- */

export function CaptionsPanel({ onGenerate, busy, error, captions, onSelect }) {
    return (
        <div className="space-y-4">
            <button
                data-testid="captions-generate-button"
                onClick={onGenerate}
                disabled={busy}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-amberx font-display text-sm font-bold text-obsidian disabled:opacity-50"
            >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                {busy ? "Listening to your video…" : "Generate auto captions"}
            </button>
            {error && <p className="rounded-xl border border-emberx/40 bg-emberx/10 px-4 py-3 text-xs text-red-200">{error}</p>}
            <p className="text-xs leading-relaxed text-slate-400">
                Listens to the audio in your video and drops editable captions on the timeline. Your
                clip is sent to our AI just for this — nothing is stored. Best under 25 MB; for big
                files, trim first.
            </p>
            {captions.length > 0 && (
                <div className="space-y-1.5">
                    <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">
                        {captions.length} CAPTIONS — EDIT THEM IN THE TEXT TAB
                    </p>
                    {captions.slice(0, 8).map((c) => (
                        <button
                            key={c.id}
                            data-testid={`caption-item-${c.id}`}
                            onClick={() => onSelect(c.id)}
                            className="flex w-full items-center gap-2 rounded-xl border border-white/10 px-3 py-2 text-left"
                        >
                            <span className="font-mono text-[10px] text-amberx">{fmtTime(c.start).slice(0, 5)}</span>
                            <span className="truncate text-sm text-slate-200">{c.content}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------------- EDIT / CLIP OPS ---------------- */

export function EditPanel({
    hasSelection,
    targetName,
    onSplit,
    onDelete,
    onDuplicate,
    rotation,
    onRotate,
    aspectId,
    onAspect,
    aspects,
}) {
    return (
        <div className="space-y-4">
            <div>
                <p className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">CLIP TOOLS</p>
                <div className="grid grid-cols-2 gap-2">
                    <button
                        data-testid="edit-split-button"
                        onClick={onSplit}
                        disabled={!hasSelection}
                        className="h-12 rounded-xl border border-white/15 text-sm font-bold text-slate-200 hover:border-amberx hover:text-amberx disabled:opacity-40"
                    >
                        ✂ Split at playhead
                    </button>
                    <button
                        data-testid="edit-duplicate-button"
                        onClick={onDuplicate}
                        disabled={!hasSelection}
                        className="h-12 rounded-xl border border-white/15 text-sm font-bold text-slate-200 hover:border-amberx hover:text-amberx disabled:opacity-40"
                    >
                        Duplicate clip
                    </button>
                    <button
                        data-testid="edit-delete-button"
                        onClick={onDelete}
                        disabled={!hasSelection}
                        className="h-12 rounded-xl border border-white/15 text-sm font-bold text-slate-200 hover:border-emberx hover:text-emberx disabled:opacity-40"
                    >
                        Delete clip
                    </button>
                    <div className="flex items-center justify-center rounded-xl border border-white/10 bg-obsidian/60 px-2 text-center font-mono text-[10px] leading-tight text-slate-500">
                        {targetName || "NO CLIP SELECTED"}
                    </div>
                </div>
            </div>
            <div>
                <p className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">ROTATE</p>
                <div className="grid grid-cols-4 gap-2">
                    {[0, 90, 180, 270].map((r) => (
                        <button
                            key={r}
                            data-testid={`rotate-select-${r}`}
                            onClick={() => onRotate(r)}
                            className={`h-11 rounded-xl border font-mono text-xs font-bold ${
                                rotation === r ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-200"
                            }`}
                        >
                            {r}°
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <p className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">CROP / FRAME</p>
                <div className="grid grid-cols-4 gap-2">
                    {aspects.map((a) => (
                        <button
                            key={a.id}
                            data-testid={`aspect-select-${a.id}`}
                            onClick={() => onAspect(a.id)}
                            className={`h-11 rounded-xl border font-mono text-xs font-bold ${
                                aspectId === a.id ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-200"
                            }`}
                        >
                            {a.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ---------------- EXPORT ---------------- */

export function ExportPanel({ opts, onOpts, onStart, disabled, mp4Ok }) {
    return (
        <div className="space-y-4">
            <div>
                <p className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">RESOLUTION</p>
                <div className="grid grid-cols-3 gap-2">
                    {["720", "1080"].map((r) => (
                        <button
                            key={r}
                            data-testid={`export-res-${r}`}
                            onClick={() => onOpts({ ...opts, res: r })}
                            className={`h-12 rounded-xl border font-mono text-sm font-bold ${
                                opts.res === r ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-200"
                            }`}
                        >
                            {r}p
                        </button>
                    ))}
                    <button
                        data-testid="export-res-4k"
                        disabled
                        title="4K needs server-side rendering — coming soon"
                        className="h-12 rounded-xl border border-white/10 font-mono text-sm font-bold text-slate-600"
                    >
                        4K
                    </button>
                </div>
            </div>
            <div>
                <p className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">FRAME RATE</p>
                <div className="grid grid-cols-3 gap-2">
                    {[24, 30, 60].map((f) => (
                        <button
                            key={f}
                            data-testid={`export-fps-${f}`}
                            onClick={() => onOpts({ ...opts, fps: f })}
                            className={`h-12 rounded-xl border font-mono text-sm font-bold ${
                                opts.fps === f ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-200"
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <p className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">FORMAT</p>
                <div className="grid grid-cols-2 gap-2">
                    <button
                        data-testid="export-format-webm"
                        onClick={() => onOpts({ ...opts, format: "webm" })}
                        className={`h-12 rounded-xl border font-mono text-sm font-bold ${
                            opts.format === "webm" ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-200"
                        }`}
                    >
                        WebM
                    </button>
                    <button
                        data-testid="export-format-mp4"
                        onClick={() => mp4Ok && onOpts({ ...opts, format: "mp4" })}
                        className={`h-12 rounded-xl border font-mono text-sm font-bold ${
                            opts.format === "mp4" ? "border-amberx bg-amberx/10 text-amberx" : "border-white/15 text-slate-200"
                        } ${mp4Ok ? "" : "opacity-40"}`}
                        title={mp4Ok ? "" : "MP4 recording is not supported by this browser"}
                    >
                        MP4 {mp4Ok ? "" : "(n/a)"}
                    </button>
                </div>
                {!mp4Ok && <p className="mt-2 text-[11px] text-slate-500">MP4 recording isn't supported by this browser — WebM plays everywhere modern.</p>}
            </div>
            <button
                data-testid="export-start-button"
                onClick={onStart}
                disabled={disabled}
                className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-amberx font-display text-base font-bold text-obsidian disabled:opacity-40"
            >
                <Sparkles className="h-5 w-5" />
                Render video
            </button>
            <p className="text-[11px] leading-relaxed text-slate-500">
                Renders right here on your device with everything baked in — filters, effects, text,
                music. Keep this tab open while it records.
            </p>
        </div>
    );
}
